// Security Alerts panel — surfaces ADR-014 bulk-access anomalies for admin
// triage. Embedded in AuditLogPage as a tab; not its own route in v1.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";
import { toast } from "sonner";
import { apiFetch } from "../lib/api.js";

type AlertStatus = "active" | "resolved" | "all";

interface BulkAccessContext {
  user_id?: string;
  view_count?: number;
  max_row_count?: number;
  sample_query_filters?: Record<string, unknown>[];
  time_window_start?: string;
  time_window_end?: string;
}

export interface SecurityAlertEntry {
  _id: string;
  tenant_id: string;
  alert_type: "bulk_access_anomaly" | string;
  severity: "info" | "warning" | "critical";
  triggered_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  context: BulkAccessContext;
}

interface AlertsResponse {
  data: SecurityAlertEntry[]; total: number; page: number; limit: number;
}

const LIMIT = 20;

const STATUS_TABS: { value: AlertStatus; label: string }[] = [
  { value: "active",   label: "Active"   },
  { value: "resolved", label: "Resolved" },
  { value: "all",      label: "All"      },
];

function buildAlertsUrl(page: number, status: AlertStatus): string {
  const params = new URLSearchParams({
    page:   String(page),
    limit:  String(LIMIT),
    status,
  });
  return `/security-alerts?${params.toString()}`;
}

function summarise(alert: SecurityAlertEntry): string {
  if (alert.alert_type === "bulk_access_anomaly") {
    const { view_count, max_row_count, user_id } = alert.context;
    const userRef = user_id ? user_id.slice(0, 8) : "unknown";
    return `User ${userRef} — ${view_count ?? "?"} views, max ${max_row_count ?? "?"} rows in 24h`;
  }
  return alert.alert_type;
}

function formatTriggered(iso: string): string {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function severityBadge(severity: SecurityAlertEntry["severity"]): JSX.Element {
  const cls = severity === "critical" ? "bg-rose-50 text-rose-700"
    : severity === "warning" ? "bg-amber-50 text-amber-700"
    : "bg-slate-100 text-slate-600";
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{severity}</span>;
}

function StatusTabs({ value, onChange }: { value: AlertStatus; onChange: (v: AlertStatus) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-1 mb-4 border border-slate-200 rounded-lg p-0.5 w-fit bg-slate-50">
      {STATUS_TABS.map((t) => (
        <button key={t.value} onClick={() => onChange(t.value)}
          className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
            value === t.value ? "bg-white text-navy-800 shadow-sm font-medium" : "text-slate-600 hover:text-slate-900"
          }`}>{t.label}</button>
      ))}
    </div>
  );
}

interface AlertsTableProps {
  items: SecurityAlertEntry[];
  onAcknowledge: (id: string) => void;
  acknowledgeBusy: boolean;
}

function AlertsTable({ items, onAcknowledge, acknowledgeBusy }: AlertsTableProps): JSX.Element {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left  px-3 py-2 font-medium">Triggered</th>
            <th className="text-left  px-3 py-2 font-medium">Type</th>
            <th className="text-left  px-3 py-2 font-medium">Severity</th>
            <th className="text-left  px-3 py-2 font-medium">Summary</th>
            <th className="text-left  px-3 py-2 font-medium">Status</th>
            <th className="text-right px-3 py-2 font-medium">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((alert) => {
            const isActive = alert.resolved_at === null;
            return (
              <tr key={alert._id} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{formatTriggered(alert.triggered_at)}</td>
                <td className="px-3 py-2 text-slate-700 font-mono text-xs">{alert.alert_type}</td>
                <td className="px-3 py-2">{severityBadge(alert.severity)}</td>
                <td className="px-3 py-2 text-slate-700">{summarise(alert)}</td>
                <td className="px-3 py-2">{isActive
                  ? <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-rose-50 text-rose-700">Active</span>
                  : <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-50 text-emerald-700">Resolved</span>}</td>
                <td className="px-3 py-2 text-right">{isActive && (
                  <button type="button" onClick={() => onAcknowledge(alert._id)} disabled={acknowledgeBusy}
                    className="text-sm text-navy-800 hover:text-navy-950 underline disabled:opacity-50">Acknowledge</button>
                )}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Pager({ page, totalPages, onPrev, onNext }: { page: number; totalPages: number; onPrev: () => void; onNext: () => void }): JSX.Element {
  return (
    <div className="flex items-center gap-4 justify-end text-sm text-slate-600 mt-4">
      <button onClick={onPrev} disabled={page <= 1}
        className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">← Prev</button>
      <span>Page {page} of {totalPages}</span>
      <button onClick={onNext} disabled={page >= totalPages}
        className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Next →</button>
    </div>
  );
}

interface PanelBodyProps {
  isLoading: boolean;
  isError: boolean;
  items: SecurityAlertEntry[];
  status: AlertStatus;
  page: number;
  totalPages: number;
  onAcknowledge: (id: string) => void;
  acknowledgeBusy: boolean;
  onPrev: () => void;
  onNext: () => void;
}

function PanelBody(p: PanelBodyProps): JSX.Element {
  if (p.isLoading) return <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>;
  if (p.isError)   return <div className="text-sm text-rose-700 py-8 text-center">Failed to load alerts.</div>;
  if (p.items.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-12 text-center border border-dashed border-slate-200 rounded-lg">
        No {p.status === "all" ? "" : p.status} alerts in this tenant.
      </div>
    );
  }
  return (
    <>
      <AlertsTable items={p.items} onAcknowledge={p.onAcknowledge} acknowledgeBusy={p.acknowledgeBusy} />
      <Pager page={p.page} totalPages={p.totalPages} onPrev={p.onPrev} onNext={p.onNext} />
    </>
  );
}

export function SecurityAlertsPanel(): JSX.Element {
  const [status, setStatus] = useState<AlertStatus>("active");
  const [page,   setPage]   = useState(1);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<AlertsResponse>({
    queryKey: ["security-alerts", status, page],
    queryFn:  () => apiFetch<AlertsResponse>(buildAlertsUrl(page, status)),
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<SecurityAlertEntry>(`/security-alerts/${id}/resolve`, { method: "PATCH" }),
    onSuccess: () => {
      toast.success("Alert acknowledged.");
      void queryClient.invalidateQueries({ queryKey: ["security-alerts"] });
    },
    onError: () => { toast.error("Failed to acknowledge alert."); },
  });

  const items      = data?.data ?? [];
  const total      = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div>
      <StatusTabs value={status} onChange={(v) => { setStatus(v); setPage(1); }} />
      <PanelBody
        isLoading={isLoading} isError={isError} items={items}
        status={status} page={page} totalPages={totalPages}
        onAcknowledge={(id) => resolveMutation.mutate(id)}
        acknowledgeBusy={resolveMutation.isPending}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
      />
    </div>
  );
}
